import React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Grid,
  Typography, Chip, Divider, Box, Slide, Button
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import PrintIcon from "@mui/icons-material/Print";
import { printDocument, field, row, section, schoolHeader } from "../../utils/printUtils";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Field = ({ label, value }) => (
  <Box mb={1.5}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={500}>{value || "-"}</Typography>
  </Box>
);

const ApplicationDetailDialog = ({ open, onClose, application }) => {
  if (!application) return null;

  const parent1 = application.parents?.find(p => p.parentType === "parent1");
  const parent2 = application.parents?.find(p => p.parentType === "parent2");
  const emergency = application.parents?.find(p => p.parentType === "emergency");

  const handlePrint = () => {
    const app = application;
    printDocument(`Application — ${app?.firstName} ${app?.lastName}`, `
      ${schoolHeader('Student Registration Application', `Application No: ${app?.applicationNumber || app?.id}`)}
      ${section('Student Information', `
        ${row(field('First Name', app?.firstName), field('Middle Name', app?.middleName), field('Last Name', app?.lastName))}
        ${row(field('Date of Birth', app?.dob), field('Gender', app?.gender), field('Student Type', app?.studentType))}
        ${row(field('Grade Applying For', app?.gradeApplyingFor), field('Previous Grade', app?.previousGrade), field('Status', `<span class="badge badge-${app?.status}">${app?.status?.replace(/_/g, ' ')}</span>`))}
        ${row(field('Address', `${app?.address || ''}, ${app?.city || ''}, ${app?.state || ''} ${app?.zip || ''}`.trim().replace(/^,\s*|,\s*$/g, '')))}
        ${app?.registrationNumber ? row(field('Registration Number', app?.registrationNumber)) : ''}
      `)}
      ${section('Parent / Guardian Information', `
        ${row(
          field('Parent 1 Name', parent1 ? `${parent1.firstName} ${parent1.lastName}` : '—'),
          field('Parent 1 Email', parent1?.email),
          field('Parent 1 Phone', parent1?.phone)
        )}
        ${parent2 ? row(
          field('Parent 2 Name', `${parent2.firstName} ${parent2.lastName}`),
          field('Parent 2 Email', parent2?.email),
          field('Parent 2 Phone', parent2?.phone)
        ) : ''}
        ${emergency ? row(field('Emergency Contact', emergency?.firstName), field('Emergency Phone', emergency?.phone)) : ''}
      `)}
    `);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth TransitionComponent={Transition}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            {application.firstName} {application.lastName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Application #{application.applicationNumber}
          </Typography>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Student Info */}
        <Typography variant="subtitle1" fontWeight="bold" color="#1a237e" gutterBottom>
          Student Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}><Field label="First Name" value={application.firstName} /></Grid>
          <Grid item xs={4}><Field label="Middle Name" value={application.middleName} /></Grid>
          <Grid item xs={4}><Field label="Last Name" value={application.lastName} /></Grid>
          <Grid item xs={4}><Field label="Date of Birth" value={application.dob} /></Grid>
          <Grid item xs={4}><Field label="Gender" value={application.gender} /></Grid>
          <Grid item xs={4}><Field label="Student Type" value={application.studentType} /></Grid>
          <Grid item xs={12}><Field label="Address" value={`${application.address}, ${application.city}, ${application.state} ${application.zip}`} /></Grid>
          <Grid item xs={4}><Field label="Previous School" value={application.previousSchool} /></Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Grade & Evaluation */}
        <Typography variant="subtitle1" fontWeight="bold" color="#1a237e" gutterBottom>
          Grade & Evaluation
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={4}><Field label="Grade Applying For" value={application.gradeApplyingFor} /></Grid>
          <Grid item xs={4}><Field label="Previous Grade" value={application.previousGrade} /></Grid>
          <Grid item xs={4}>
            <Field label="Skipped Grade" value={application.isSkippedGrade ? "Yes" : "No"} />
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Box mt={0.5}>
              <Chip label={application.status.replace(/_/g, " ")} size="small"
                sx={{ textTransform: "capitalize", fontWeight: 600 }} />
            </Box>
          </Grid>
          {application.registrationNumber && (
            <Grid item xs={4}><Field label="Registration Number" value={application.registrationNumber} /></Grid>
          )}
          <Grid item xs={4}><Field label="Zelle Transaction ID" value={application.zelleTransactionId} /></Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Parent Info */}
        <Typography variant="subtitle1" fontWeight="bold" color="#1a237e" gutterBottom>
          Parent Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="subtitle2" mb={1}>Parent 1</Typography>
            <Field label="Name" value={parent1 ? `${parent1.firstName} ${parent1.lastName}` : "-"} />
            <Field label="Email" value={parent1?.email} />
            <Field label="Phone" value={parent1?.phone} />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" mb={1}>Parent 2</Typography>
            <Field label="Name" value={parent2 ? `${parent2.firstName} ${parent2.lastName}` : "-"} />
            <Field label="Email" value={parent2?.email} />
            <Field label="Phone" value={parent2?.phone} />
          </Grid>
        </Grid>
        {emergency && (
          <Box mt={2}>
            <Typography variant="subtitle2" mb={1}>Emergency Contact</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}><Field label="Name" value={emergency.firstName} /></Grid>
              <Grid item xs={6}><Field label="Phone" value={emergency.phone} /></Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={handlePrint} startIcon={<PrintIcon />}>
          Print / PDF
        </Button>
        <Button onClick={onClose} variant="outlined" color="inherit">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApplicationDetailDialog;
